import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Typography,
} from '@mui/material';
import { Button, ToggleSwitch } from '../../primitives';
import { getCsrfToken } from '../../../utils/csrf';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

/**
 * Amenity configuration - maps facility boolean fields to display labels
 */
const AMENITIES = [
  { key: 'is_parking_available', label: 'Parking Available' },
  { key: 'is_restrooms_available', label: 'Locker Rooms Available' },
  { key: 'is_wifi_available', label: 'WiFi Available' },
  { key: 'is_food_available', label: 'Food Available' },
  { key: 'is_shower_available', label: 'Shower Available' },
  { key: 'is_lounge_available', label: 'Lounge Available' },
];

/**
 * FacilityBooleanForm - Dialog form for editing facility amenities
 * 
 * @param {boolean} open - Whether the dialog is open
 * @param {function} onClose - Callback when dialog closes
 * @param {function} onSave - Callback when amenities are saved (receives updated facility)
 * @param {object} facility - Facility object to edit
 */
const FacilityBooleanForm = ({ open, onClose, onSave, facility }) => {
  const [formData, setFormData] = useState({
    is_parking_available: false,
    is_restrooms_available: false,
    is_wifi_available: false,
    is_food_available: false,
    is_shower_available: false,
    is_lounge_available: false,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Populate form when modal opens or facility changes
  useEffect(() => {
    if (open && facility) {
      setFormData({
        is_parking_available: facility.is_parking_available || false,
        is_restrooms_available: facility.is_restrooms_available || false,
        is_wifi_available: facility.is_wifi_available || false,
        is_food_available: facility.is_food_available || false,
        is_shower_available: facility.is_shower_available || false,
        is_lounge_available: facility.is_lounge_available || false,
      });
      setErrors({});
      setErrorMessage('');
    }
  }, [open, facility]);

  const handleToggleChange = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));
    // Clear error for this field when user changes value
    if (errors[key]) {
      setErrors((prev) => ({
        ...prev,
        [key]: '',
      }));
    }
    // Clear general error message
    if (errorMessage) {
      setErrorMessage('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');
    setErrors({});

    try {
      const csrfToken = getCsrfToken();
      const headers = {
        'Content-Type': 'application/json',
      };
      if (csrfToken) {
        headers['X-CSRFToken'] = csrfToken;
      }

      const response = await fetch(`${API_BASE_URL}/facilities/${facility.id}/`, {
        method: 'PATCH',
        headers,
        credentials: 'include',
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle validation errors
        if (data.is_parking_available || data.is_restrooms_available || data.is_wifi_available || 
            data.is_food_available || data.is_shower_available || data.is_lounge_available) {
          setErrors(data);
        } else {
          setErrorMessage(data.error || data.message || 'Failed to update amenities. Please try again.');
        }
        setLoading(false);
        return;
      }

      // Success - call parent callback with updated facility
      onSave(data);
      setLoading(false);
    } catch (err) {
      setErrorMessage('Network error. Please check your connection and try again.');
      console.error('Error updating facility amenities:', err);
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Amenities</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            {AMENITIES.map((amenity) => (
              <Box
                key={amenity.key}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: '#000000',
                    fontSize: '0.875rem',
                    fontWeight: 400,
                    flexShrink: 0,
                  }}
                >
                  {amenity.label}
                </Typography>
                <Box sx={{ flexShrink: 0, minWidth: 200 }}>
                  <ToggleSwitch
                    value={formData[amenity.key]}
                    onChange={(value) => handleToggleChange(amenity.key, value)}
                    sx={{ width: '100%' }}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} color="inherit" />
                <span>Saving...</span>
              </Box>
            ) : (
              'Save'
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default FacilityBooleanForm;
